import request from './config'

/**
 * 睡眠处方相关API
 */
export const sleepPrescriptionApi = {
  /**
   * 根据患者ID查询睡眠处方列表
   * @param {string|number} patientId - 患者ID
   * @param {Object} [params] - 查询参数
   * @param {number} [params.current=1] - 当前页码，默认1
   * @param {number} [params.size=10] - 每页大小，默认10
   * @returns {Promise<Object>} API响应
   */
  async getPatientSleepPrescriptions(patientId, params = {}) {
    try {
      console.log('获取患者睡眠处方列表，患者ID:', patientId, '查询参数:', params)

      // 验证必需参数
      if (!patientId) {
        throw new Error('患者ID不能为空')
      }

      // 构建查询参数
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      console.log('发送请求到 /sleep-prescription/patient/' + patientId)
      console.log('查询参数:', queryParams)

      // 发送GET请求
      const response = await request.get(`/sleep-prescription/patient/${patientId}`, {
        params: queryParams
      })

      console.log('获取患者睡眠处方列表API原始响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        const records = data.data?.records || []
        const total = data.data?.total || 0
        const pages = data.data?.pages || 0
        
        console.log('原始睡眠处方数据条数:', records.length)
        console.log('总记录数:', total)

        // 转换数据格式以适配前端显示
        const formattedPrescriptions = records.map((prescription, index) => {
          console.log(`处理第${index + 1}个睡眠处方:`, prescription)

          // 格式化时间显示
          const formatTime = (timeStr) => {
            if (!timeStr) return ''
            // 如果是HH:mm:ss格式，直接返回HH:mm
            if (timeStr.includes(':')) {
              const parts = timeStr.split(':')
              return `${parts[0]}:${parts[1]}`
            }
            return timeStr
          }

          // 格式化日期时间显示
          const formatDateTime = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            } catch (e) {
              return dateTimeStr
            }
          }

          // 格式化日期显示
          const formatDate = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              return date.toLocaleDateString('zh-CN')
            } catch (e) {
              return dateTimeStr
            }
          }

          // 计算睡眠时长（小时）
          const calculateSleepHours = (durationStr) => {
            if (!durationStr) return 0
            try {
              const parts = durationStr.split(':')
              const hours = parseInt(parts[0]) || 0
              const minutes = parseInt(parts[1]) || 0
              return hours + (minutes / 60)
            } catch (e) {
              return 0
            }
          }

          return {
            id: prescription.id,
            patientId: prescription.patientId,
            bedtime: formatTime(prescription.bedtime), // 上床时间
            wakeUpTime: formatTime(prescription.wakeUpTime), // 起床时间
            sleepDuration: calculateSleepHours(prescription.prescriptionSleepDuration), // 睡眠时长（小时）
            prescribeTime: formatDateTime(prescription.prescribeTime), // 处方创建时间
            endTime: formatDate(prescription.prescriptionEndTime), // 处方结束日期
            isCurrentUse: prescription.isUse === 1, // 是否当前使用
            status: prescription.isUse === 1 ? '使用中' : '未使用', // 状态文本
            updateTime: formatDateTime(prescription.updateTime), // 更新时间
            
            // 保留原始数据以便详情查看
            originalData: prescription
          }
        })

        console.log(`格式化睡眠处方数据完成，共${formattedPrescriptions.length}条记录`)

        return {
          success: true,
          message: data.message || '获取患者睡眠处方列表成功',
          data: formattedPrescriptions,
          total: total,
          pages: pages,
          current: params.current || 1,
          size: params.size || 10
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取患者睡眠处方列表失败'
        console.warn('获取患者睡眠处方列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取患者睡眠处方列表异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: [],
          total: 0
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: [],
          total: 0
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取患者睡眠处方列表失败',
        data: [],
        total: 0
      }
    }
  },

  /**
   * 修改睡眠处方
   * @param {Object} prescriptionData - 睡眠处方数据
   * @param {number} prescriptionData.id - 睡眠处方记录ID
   * @param {number} prescriptionData.patientId - 患者ID
   * @param {string} prescriptionData.bedtime - 上床时间（HH:mm:ss格式）
   * @param {string} prescriptionData.wakeUpTime - 起床时间（HH:mm:ss格式）
   * @param {string} prescriptionData.prescriptionSleepDuration - 处方睡眠时长（HH:mm:ss格式）
   * @param {string} prescriptionData.prescriptionEndTime - 处方结束时间（ISO格式）
   * @param {number} prescriptionData.isUse - 是否当前使用（1-是，0-否）
   * @returns {Promise<Object>} API响应
   */
  async updateSleepPrescription(prescriptionData) {
    try {
      console.log('修改睡眠处方，请求参数:', prescriptionData)

      // 验证必需参数
      const requiredFields = ['id', 'patientId', 'bedtime', 'wakeUpTime', 'prescriptionSleepDuration', 'prescriptionEndTime', 'isUse']
      for (const field of requiredFields) {
        if (prescriptionData[field] === undefined || prescriptionData[field] === null) {
          throw new Error(`缺少必需参数：${field}`)
        }
      }

      // 构建请求体
      const requestBody = {
        id: parseInt(prescriptionData.id),
        patientId: parseInt(prescriptionData.patientId),
        bedtime: prescriptionData.bedtime,
        wakeUpTime: prescriptionData.wakeUpTime,
        prescriptionSleepDuration: prescriptionData.prescriptionSleepDuration,
        prescriptionEndTime: prescriptionData.prescriptionEndTime,
        isUse: parseInt(prescriptionData.isUse)
      }

      console.log('发送请求到 /sleep-prescription/update')
      console.log('请求体:', requestBody)

      // 发送PUT请求
      const response = await request.put('/sleep-prescription/update', requestBody)

      console.log('修改睡眠处方API原始响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        console.log('睡眠处方修改成功:', data.data)

        return {
          success: true,
          message: data.message || '睡眠处方修改成功',
          data: data.data
        }
      } else {
        // 修改失败
        const errorMessage = data.message || '睡眠处方修改失败'
        console.warn('睡眠处方修改失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('修改睡眠处方异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试'
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录'
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '睡眠处方修改失败'
      }
    }
  },

  /**
   * 删除睡眠处方
   * @param {string|number} prescriptionId - 睡眠处方ID
   * @returns {Promise<Object>} API响应
   */
  async deleteSleepPrescription(prescriptionId) {
    try {
      console.log('删除睡眠处方，处方ID:', prescriptionId)

      // 验证必需参数
      if (!prescriptionId) {
        throw new Error('睡眠处方ID不能为空')
      }

      console.log('发送请求到 /sleep-prescription/delete')
      console.log('查询参数: id =', prescriptionId)

      // 发送DELETE请求
      const response = await request.delete('/sleep-prescription/delete', {
        params: {
          id: prescriptionId
        }
      })

      console.log('删除睡眠处方API原始响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        console.log('睡眠处方删除成功:', data.data)

        return {
          success: true,
          message: data.message || '睡眠处方删除成功',
          data: data.data
        }
      } else {
        // 删除失败
        const errorMessage = data.message || '睡眠处方删除失败'
        console.warn('睡眠处方删除失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('删除睡眠处方异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试'
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录'
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '睡眠处方删除失败'
      }
    }
  }
}

export default sleepPrescriptionApi
